import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { 
  ArrowLeft, 
  ArrowLeftRight, 
  Building, 
  User, 
  FileText, 
  Calendar, 
  Phone, 
  Mail, 
  MapPin,
  CreditCard,
  Users,
  Briefcase,
  CheckCircle,
  XCircle,
  Clock,
  Download,
  Printer
} from "lucide-react";
import "./TransferView.css";

const url = process.env.REACT_APP_BACKEND_URL;

const TransferView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [transfer, setTransfer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTransferDetails();
  }, [id]);

  const fetchTransferDetails = async () => {
    try {
      console.log("Fetching transfer details for ID:", id);
      const response = await axios.get(`${url}/api/transfer/${id}`);
      console.log("Transfer details:", response.data);
      
      if (response.data.success) {
        setTransfer(response.data.data);
      } else {
        toast.error("Failed to fetch transfer details");
        navigate("/ViewTransfer");
      }
    } catch (error) {
      console.error("Error fetching transfer:", error);
      toast.error(error.response?.data?.message || "Failed to fetch transfer details");
      navigate("/ViewTransfer");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { 
        class: "status-pending", 
        text: "Pending", 
        icon: <Clock size={16} /> 
      },
      approved: { 
        class: "status-approved", 
        text: "Approved", 
        icon: <CheckCircle size={16} /> 
      },
      rejected: { 
        class: "status-rejected", 
        text: "Rejected", 
        icon: <XCircle size={16} /> 
      },
      completed: { 
        class: "status-completed", 
        text: "Completed", 
        icon: <CheckCircle size={16} /> 
      },
    };
    return badges[status] || { class: "status-pending", text: status, icon: <Clock size={16} /> };
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  const formatDateTime = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="transfer-view-wrapper">
        <div className="loading-container">
          <div className="spinner-large"></div>
          <p>Loading transfer details...</p>
        </div>
      </div>
    );
  }

  if (!transfer) {
    return (
      <div className="transfer-view-wrapper">
        <div className="error-container">
          <XCircle size={48} />
          <h3>Transfer Not Found</h3>
          <p>The transfer record you're looking for doesn't exist.</p>
          <button onClick={() => navigate("/ViewTransfer")} className="back-btn">
            Back to Transfers
          </button>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusBadge(transfer.transfer_status);

  return (
    <div className="transfer-view-wrapper">
      <div className="transfer-view-container">
        {/* Header */}
        <div className="view-header">
          <button onClick={() => navigate("/ViewTransfer")} className="back-button">
            <ArrowLeft size={20} /> Back to Transfers
          </button>
          <div className="header-actions">
            <button onClick={handlePrint} className="print-button">
              <Printer size={18} /> Print
            </button>
          </div>
        </div>

        {/* Title Section */}
        <div className="title-section">
          <div className="title-icon">
            <ArrowLeftRight size={32} />
          </div>
          <div className="title-content">
            <h1>Transfer Details</h1>
            <div className="transfer-id">
              Transfer #{transfer.transfer_no}
              <span className={`status-badge ${statusInfo.class}`}>
                {statusInfo.icon}
                {statusInfo.text}
              </span>
            </div>
          </div>
        </div>

        {/* Transfer Information Card */}
        <div className="info-card">
          <h3>
            <FileText size={20} />
            Transfer Information
          </h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Transfer Date:</label>
              <span>{formatDateTime(transfer.transfer_date)}</span>
            </div>
            <div className="info-item">
              <label>Transfer Type:</label>
              <span className="capitalize">{transfer.transfer_type?.replace("_", " ")}</span>
            </div>
            {transfer.transfer_reason && (
              <div className="info-item full-width">
                <label>Transfer Reason:</label>
                <span>{transfer.transfer_reason}</span>
              </div>
            )}
            {transfer.remarks && (
              <div className="info-item full-width">
                <label>Remarks:</label>
                <span>{transfer.remarks}</span>
              </div>
            )}
          </div>
        </div>

        {/* Customer Information Card */}
        <div className="info-card">
          <h3>
            <User size={20} />
            Customer Information
          </h3>
          <div className="info-grid">
            <div className="info-item">
              <label>Full Name:</label>
              <span className="customer-name">{transfer.original_customer_id?.fullname}</span>
            </div>
            <div className="info-item">
              <label>
                <Phone size={14} />
                Phone:
              </label>
              <span>{transfer.original_customer_id?.phone}</span>
            </div>
            <div className="info-item">
              <label>
                <Mail size={14} />
                Email:
              </label>
              <span>{transfer.original_customer_id?.email}</span>
            </div>
            <div className="info-item full-width">
              <label>
                <MapPin size={14} />
                Address:
              </label>
              <span>{transfer.original_customer_id?.address}</span>
            </div>
          </div>
        </div>

        {/* Contract Transfer Details */}
        <div className="transfer-details-grid">
          {/* Original Client Card */}
          <div className="info-card">
            <h3>
              <Building size={20} />
              Original Client
            </h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Client Name:</label>
                <span>{transfer.original_client_id?.name}</span>
              </div>
              <div className="info-item">
                <label>Location:</label>
                <span>{transfer.original_client_id?.location || "N/A"}</span>
              </div>
              <div className="info-item">
                <label>Manager:</label>
                <span>{transfer.original_client_id?.manager_name || "N/A"}</span>
              </div>
              <div className="info-item">
                <label>Contact:</label>
                <span>{transfer.original_client_id?.manager_contact || "N/A"}</span>
              </div>
              <div className="info-item">
                <label>Contract #:</label>
                <span>#{transfer.original_contract_id?.no}</span>
              </div>
              <div className="info-item">
                <label>Contract Period:</label>
                <span>
                  {formatDate(transfer.original_contract_start)} - {formatDate(transfer.original_contract_end)}
                </span>
              </div>
            </div>
          </div>

          {/* New Client Card */}
          <div className="info-card highlight-card">
            <h3>
              <Building size={20} />
              New Client
              <span className="new-badge">NEW</span>
            </h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Client Name:</label>
                <span className="new-client-name">{transfer.new_client_id?.name}</span>
              </div>
              <div className="info-item">
                <label>Location:</label>
                <span>{transfer.new_client_id?.location || "N/A"}</span>
              </div>
              <div className="info-item">
                <label>Manager:</label>
                <span>{transfer.new_client_id?.manager_name || "N/A"}</span>
              </div>
              <div className="info-item">
                <label>Contact:</label>
                <span>{transfer.new_client_id?.manager_contact || "N/A"}</span>
              </div>
              <div className="info-item">
                <label>New Contract #:</label>
                <span className="contract-number">#{transfer.new_contract_id?.no}</span>
              </div>
              <div className="info-item">
                <label>New Contract Period:</label>
                <span>
                  {formatDate(transfer.new_contract_start)} - {formatDate(transfer.new_contract_end)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Job & Financial Details */}
        <div className="info-card">
          <h3>
            <Briefcase size={20} />
            Job & Financial Details
          </h3>
          <div className="info-grid three-columns">
            <div className="info-item">
              <label>Job Title:</label>
              <span>{transfer.transfer_job_name || transfer.original_job_id?.category_name || "N/A"}</span>
            </div>
            <div className="info-item">
              <label>Job Section:</label>
              <span>{transfer.transfer_job_section || "N/A"}</span>
            </div>
            <div className="info-item">
              <label>New Salary:</label>
              <span className="salary-amount">${transfer.new_salary?.toLocaleString() || "0"}</span>
            </div>
          </div>
        </div>

        {/* Bank Details */}
        {transfer.new_bank_name && (
          <div className="info-card">
            <h3>
              <CreditCard size={20} />
              Bank Details
            </h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Bank Name:</label>
                <span>{transfer.new_bank_name}</span>
              </div>
              <div className="info-item">
                <label>Account Name:</label>
                <span>{transfer.new_account_name}</span>
              </div>
              <div className="info-item">
                <label>Account Number:</label>
                <span>{transfer.new_account_no}</span>
              </div>
            </div>
          </div>
        )}

        {/* Next of Kin Details */}
        {(transfer.new_nok_name || transfer.new_damin_name) && (
          <div className="info-card">
            <h3>
              <Users size={20} />
              Contact Persons
            </h3>
            <div className="info-grid two-columns">
              {transfer.new_nok_name && (
                <div className="sub-section">
                  <h4>Next of Kin</h4>
                  <div className="info-item">
                    <label>Name:</label>
                    <span>{transfer.new_nok_name}</span>
                  </div>
                  <div className="info-item">
                    <label>Contact:</label>
                    <span>{transfer.new_nok_contact}</span>
                  </div>
                </div>
              )}
              {transfer.new_damin_name && (
                <div className="sub-section">
                  <h4>Guarantor</h4>
                  <div className="info-item">
                    <label>Name:</label>
                    <span>{transfer.new_damin_name}</span>
                  </div>
                  <div className="info-item">
                    <label>Contact:</label>
                    <span>{transfer.new_damin_contact}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Approval Information */}
        {transfer.approval_date && (
          <div className="info-card">
            <h3>Approval Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Approved By:</label>
                <span>{transfer.approved_by?.name || "System"}</span>
              </div>
              <div className="info-item">
                <label>Approval Date:</label>
                <span>{formatDateTime(transfer.approval_date)}</span>
              </div>
              {transfer.approval_remarks && (
                <div className="info-item full-width">
                  <label>Approval Remarks:</label>
                  <span>{transfer.approval_remarks}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Processing Information */}
        {transfer.processing_date && (
          <div className="info-card">
            <h3>Processing Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Processed By:</label>
                <span>{transfer.processed_by?.name || "System"}</span>
              </div>
              <div className="info-item">
                <label>Processing Date:</label>
                <span>{formatDateTime(transfer.processing_date)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Footer Actions */}
        <div className="view-footer">
          <button onClick={() => navigate("/ViewTransfer")} className="secondary-btn">
            Back to List
          </button>
          <button onClick={handlePrint} className="print-btn">
            <Printer size={18} /> Print Details
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransferView;